import React, { useState } from "react";
import styles from "./InvoiceDetailsModal.module.scss";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import {
  FetchInvoiceMetaType,
  GetProcessContentType,
} from "../../../Types/ticketProcessingTypes";
import TicketProcessingServices from "../../../services/ticketProcessing.service";
import { message, Modal } from "antd";
import { getInvoiceDetailsModalContent, getProcessContents } from "./constants";
import { ButtonSize, ButtonType } from "../../../enums/ButtonAttributesEnums";
import clsx from "clsx";
import TNButton from "../../../reusable-components/TNButton";
import GenerateSuccessModal from "../GenerateSuccessModal";
import TNLoader from "../../../reusable-components/TNLoader";
import { duration } from "moment";
import { textConsts } from "../ApprovedTickets/constant";
import { useQueryClient } from "@tanstack/react-query";
import { QueryKeys } from "../../../constants/QueryKeys";

interface InvoiceDetailsModalProps {
  isOpen: boolean;
  handleClose?: () => void;
  dateRanges?: FetchInvoiceMetaType[];
  isRange?: boolean;
  deselectedRows?: () => void;
  invoiceMeta?: GetProcessContentType;
  hasInvoiceAccess?: boolean;
}

const InvoiceDetailsModal = ({
  isOpen,
  handleClose,
  dateRanges,
  isRange = false,
  invoiceMeta,
  hasInvoiceAccess = false,
}: InvoiceDetailsModalProps) => {
  const queryClient = useQueryClient();
  const { GenerateInvoices } = TicketProcessingServices();
  const { mutateAsync: generateInvoices, isPending: generateInvoicesPending } =
    GenerateInvoices();
  const [invoiceGenerationSuccessOpen, setInvoiceGenerationSuccessOpen] =
    useState(false);
  const [ticketCount, setTicketCount] = useState(0);
  const handleSuccessOpen = () => {
    setInvoiceGenerationSuccessOpen(true);
  };
  const handleSuccessClose = () => {
    setInvoiceGenerationSuccessOpen(false);
  };

  const handleInvoiceGeneration = async () => {
    const payloadArray = dateRanges;
    await generateInvoices(payloadArray, {
      onSuccess: (data) => {
        handleClose();
        const invoicesGenerated = data?.summary?.invoiceCount;
        const partialGenerationTrue = data?.summary?.partialInvoice ?? false;
        setTicketCount(invoicesGenerated);
        if (!!invoicesGenerated) {
          if (partialGenerationTrue)
            message.warning({
              content: textConsts.PARTIAL_BATCH_FAILURE,
              duration: 5,
            });
          handleSuccessOpen();
        } else {
          message.error({
            content: textConsts.TOTAL_BATCH_FAILURE,
            duration: 5,
          });
        }
        queryClient.invalidateQueries([QueryKeys.FETCH_TICKET_PROCESSING_KEY]);
      },
      onError: (error) => {
        message.warn({
          content: error?.response?.data?.message ?? textConsts.SOMETHING_WENT_WRONG,
          duration: 5,
        })
      },
    });
  };

  const processContents = getProcessContents(invoiceMeta);

  const invoiceDetailsModalParams = getInvoiceDetailsModalContent({
    fromDate: dateRanges?.[0]?.startDate,
    toDate: dateRanges?.[0]?.endDate,
    isRange: isRange,
  });

  const invoiceDetailsFooter = (
    <div className={styles.modalFooter}>
      <div className={styles.notes}>
        <span className={styles.noteHeader}>
          {invoiceDetailsModalParams.noteHeader}
        </span>
        &nbsp;
        <span>{invoiceDetailsModalParams.noteContents}</span>
      </div>
      <div>
        <TNButton
          type={ButtonType.DEFAULT}
          size={ButtonSize.MEDIUM}
          onClick={handleClose}
          text={invoiceDetailsModalParams.cancelButtonLabel}
        />
        <TNButton
          type={ButtonType.PRIMARY}
          size={ButtonSize.MEDIUM}
          onClick={handleInvoiceGeneration}
          text={invoiceDetailsModalParams.okButtonLabel}
        />
      </div>
    </div>
  );

  const invoiceDetailsModalContent = (
    <div>
      <div className={styles.questionHeader}>
        {invoiceDetailsModalParams.question}
      </div>
      <ConditionalRender visible={!isRange}>
        <div className={styles.invoiceDates}>
          {invoiceDetailsModalParams.date}
        </div>
      </ConditionalRender>
      <div className={styles.processedHeader}>
        {invoiceDetailsModalParams.toBeProcessedHeader}
        <div className={styles.processContents}>
          {processContents?.map?.((content, index) => (
            <div
              className={clsx(styles.individualContent, {
                [styles.notLast]: index !== processContents.length - 1,
              })}
            >
              <div lassName={styles.contentValue}>{content?.value}</div>
              <div className={styles.contentLabel}>{content?.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <TNLoader isLoading={generateInvoicesPending} />
      <Modal
        visible={isOpen}
        destroyOnClose
        title={invoiceDetailsModalParams.title}
        okText={invoiceDetailsModalParams.okButtonLabel}
        footer={invoiceDetailsFooter}
        width={invoiceDetailsModalParams.width}
        onCancel={handleClose}
        maskClosable={false}
      >
        {invoiceDetailsModalContent}
      </Modal>
      <GenerateSuccessModal
        isOpen={invoiceGenerationSuccessOpen}
        handleClose={handleSuccessClose}
        ticketCount={ticketCount}
        hasInvoiceAccess={hasInvoiceAccess}
      />
    </>
  );
};

export default InvoiceDetailsModal;
